import { EmptyState } from '@kerfjs/ui/empty-state';
import { type SafeHtml } from 'kerfjs';

import { assumptionFxCodes, shellSearch } from '../../data/state.js';
import type { OrganizationPage } from '../../lib/settings-nav.js';
import { matchesSettingsSearch } from '../../lib/settings-search.js';
import { AiUsageView } from './ai-usage-view.js';
import { CurrenciesView } from './currencies-view.js';
import { DataPrivacyView } from './data-privacy-view.js';
import { LandedCostAssumptionsView } from './landed-cost-assumptions-view.js';
import { type MemberRow, MembersView } from './members-view.js';
import { OrgProfileView } from './org-profile-view.js';
import { num } from './settings-value.js';
import { type SiteRow, SitesView } from './sites-view.js';
import { VerdictThresholdsView } from './verdict-thresholds-view.js';

/** One organization settings page, selected by the sidebar (HS2-91S2YF). */
export function OrgSettingsPage({
  settings: s,
  page,
}: {
  settings: Record<string, unknown>;
  page: OrganizationPage;
}): SafeHtml | null {
  const profile = s.profile as Record<string, unknown>;
  switch (page) {
    case 'profile':
      return <OrgProfileView profile={profile} />;
    case 'members': {
      const members = ((s.members ?? []) as MemberRow[]).filter((member) =>
        matchesSettingsSearch([member.name, member.email, ...member.roles, member.status], shellSearch.value),
      );
      return members.length === 0 && shellSearch.value.trim() !== '' ? (
        <EmptyState title="No Matching Members" detail="Try another search." />
      ) : (
        <MembersView members={members} />
      );
    }
    case 'sites':
      return <SitesView sites={(s.sites ?? []) as SiteRow[]} />;
    case 'verdict-thresholds':
      return <VerdictThresholdsView thresholds={(profile.verdictThresholds ?? {}) as Record<string, unknown>} />;
    case 'landed-cost':
      return <LandedCostAssumptionsView assumptions={(s.assumptions ?? {}) as Record<string, unknown>} />;
    case 'currencies':
      return (
        <CurrenciesView
          assumptions={(s.assumptions ?? {}) as Record<string, unknown>}
          baseCurrency={typeof profile.baseCurrency === 'string' ? profile.baseCurrency : 'PKR'}
          currencies={assumptionFxCodes.value}
        />
      );
    case 'ai-usage':
      return <AiUsageView />;
    case 'data-privacy':
      return <DataPrivacyView orgName={num(profile.name)} />;
    // Knowledge Base and Vendors render their own views (HS2-91S2YF).
    case 'knowledge-base':
    case 'vendors':
      return null;
  }
}
